import { debounce, isEqual, isNull } from 'lodash';
import React, {
  ComponentProps,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  ClearIndicatorProps,
  components,
  DropdownIndicatorProps,
  GroupBase,
  OptionsOrGroups,
} from 'react-select';
import AsyncSelect from 'react-select/async';
import styles from './Select.module.scss';
import {
  defaultStyle,
  disabledStyle,
  errorStyle,
  warningStyle,
} from './SelectConfig';
import { IOptions } from '@/data/models/option';
import MultiTrackChangeIndicator from '@/components/common/MultiTrackChangeIndicator/MultiTrackChangeIndicator';
import ReadOnlyField from '../ReadOnlyField/ReadOnlyField';
import { VALIDATION_STATE_WARNING } from '@/utils/validationErrors';

function DropdownIndicator(props: DropdownIndicatorProps) {
  return (
    <components.DropdownIndicator
      {...props}
      className={styles['dropdown-container']}
    >
      <i className={`fa-solid fa-angle-down ${styles['icon']}`}></i>
    </components.DropdownIndicator>
  );
}

function ClearIndicator(props: ClearIndicatorProps) {
  return (
    <components.ClearIndicator {...props}>
      <i className={`fas fa-times ${styles['clear-icon']}`}></i>
    </components.ClearIndicator>
  );
}

type SelectBaseProps = {
  id: string;
  labelTitle?: string;
  labelClass?: string;
  loadOptions?: ComponentProps<AsyncSelect>['loadOptions'];
  loadOptionsDebounceMs?: number;
  initialOption?: any;
  className?: string;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  onChange?: (option: any) => void;
  hasErrors?: boolean;
  validationState?: string;
  isRequired?: boolean;
  fieldValidationKey?: string;
  showValidationErrors?: boolean;
  errorMessage?: string;
  baseOptions?: IOptions<any>[];
  multitrack?: any;
  showChangeIndicator?: boolean;
  handleUndo?(value: any): void;
};

type SelectProps = SelectBaseProps &
  Omit<ComponentProps<AsyncSelect>, keyof SelectBaseProps>;

type LoadOptionsCallback = (
  options: OptionsOrGroups<any, GroupBase<any>>
) => void;

export default function Select({
  id,
  labelTitle,
  labelClass = '',
  loadOptions,
  loadOptionsDebounceMs = 150,
  initialOption,
  className = '',
  isDisabled,
  isReadOnly,
  onChange = () => {},
  hasErrors,
  validationState,
  isRequired,
  fieldValidationKey,
  showValidationErrors = true,
  errorMessage,
  baseOptions,
  multitrack,
  showChangeIndicator,
  handleUndo,
  ...attrs
}: SelectProps) {
  const [changeEvent, setChangeEvent] = useState(false);
  const [clickEvent, setClickEvent] = useState(false);
  const normalizeOption = (option: any) => option ?? null;
  const [selectedOption, setSelectedOption] = useState<any>(
    normalizeOption(initialOption)
  );
  const { t } = useTranslation();
  const loadOptionsRef = useRef(loadOptions);
  const baseOptionsRef = useRef(baseOptions);

  useEffect(() => {
    const nextOption = normalizeOption(initialOption);
    setSelectedOption((prev: any) =>
      isEqual(prev, nextOption) ? prev : nextOption
    );
  }, [initialOption]);

  useEffect(() => {
    loadOptionsRef.current = loadOptions;
  }, [loadOptions]);

  useEffect(() => {
    baseOptionsRef.current = baseOptions;
  }, [baseOptions]);

  const debouncedLoadOptions = useMemo(
    () =>
      debounce((inputValue: string, callback: LoadOptionsCallback) => {
        if (loadOptionsRef.current) {
          return loadOptionsRef.current(inputValue, callback as any);
        }

        const normalizedInput = inputValue.trim().toLowerCase();
        const options = baseOptionsRef.current ?? [];

        callback(
          normalizedInput
            ? options.filter((option) =>
                String(option.label).toLowerCase().includes(normalizedInput)
              )
            : options
        );
      }, loadOptionsDebounceMs),
    [loadOptionsDebounceMs]
  );

  useEffect(() => {
    return () => {
      debouncedLoadOptions.cancel();
    };
  }, [debouncedLoadOptions]);

  const hasSelectedOption = (option: any) =>
    Array.isArray(option) ? option.length > 0 : !!option;

  const getErrorMessage = () => {
    if (!showValidationErrors) return;
    if (errorMessage) return errorMessage;
    if (isRequired && !hasSelectedOption(selectedOption))
      return t(
        `validationAlert.${fieldValidationKey}.NullorEmpty.ErrorMessage`
      );
  };

  const undoChanges = useCallback(() => {
    if (handleUndo) {
      if (!baseOptions) return;
      const originalValue = multitrack?.originalValue;
      const originalOption = baseOptions?.find((option) =>
        isEqual(option.value, originalValue)
      );

      setSelectedOption(
        originalValue === 'Mixed' || isNull(originalValue)
          ? null
          : originalOption ?? null
      );

      handleUndo(originalValue);
    }
  }, [baseOptions, multitrack]);

  const getReadOnlyValue = (option: any) => {
    if (Array.isArray(option)) {
      return option
        .map((item) => item?.label)
        .filter(Boolean)
        .join(', ');
    }

    return option?.label ?? option;
  };

  const fieldValid =
    !showValidationErrors ||
    ((!hasErrors || changeEvent) &&
      (!isRequired || hasSelectedOption(selectedOption)));

  const loadingMessage =
    attrs.loadingMessage ?? (() => t('common.generic.loading'));
  const noOptionsMessage =
    attrs.noOptionsMessage ?? (() => t('common.generic.noneFound'));
  const isWarningValidation = validationState === VALIDATION_STATE_WARNING;
  const invalidStyle = isWarningValidation ? warningStyle : errorStyle;
  const errorPopupClassName = isWarningValidation
    ? ' col-sm-2 popUp-container-warning'
    : ' col-sm-2 popUp-container';

  return (
    <>
      {!isReadOnly ? (
        <div className="select-input-container">
          {labelTitle ? (
            <label htmlFor={id} className={labelClass}>
              {labelTitle}
            </label>
          ) : null}
          {multitrack && showChangeIndicator ? (
            <MultiTrackChangeIndicator
              show={
                !isDisabled &&
                !isEqual(multitrack?.value, multitrack?.originalValue)
              }
              onClick={() => undoChanges()}
            ></MultiTrackChangeIndicator>
          ) : null}
          <div className={`item-text-right ${className}`}>
            <AsyncSelect
              id={id}
              name={id}
              {...attrs}
              loadingMessage={loadingMessage}
              noOptionsMessage={noOptionsMessage}
              onFocus={() => {
                setClickEvent(true);
              }}
              onChange={(e) => {
                setChangeEvent(true);
                setSelectedOption(e);
                onChange(e);
              }}
              onBlur={() => {
                setClickEvent(false);
              }}
              loadOptions={debouncedLoadOptions}
              value={selectedOption}
              styles={
                isDisabled
                  ? disabledStyle
                  : !fieldValid
                    ? invalidStyle
                    : defaultStyle
              }
              isDisabled={isDisabled}
              defaultOptions={baseOptions?.length ? baseOptions : true}
              menuShouldScrollIntoView={false}
              components={{
                DropdownIndicator,
                ClearIndicator,
                IndicatorSeparator: null,
              }}
            ></AsyncSelect>
          </div>
          {!fieldValid && clickEvent ? (
            <div className={errorPopupClassName}>{getErrorMessage()}</div>
          ) : null}
        </div>
      ) : (
        <ReadOnlyField
          id={id}
          labelTitle={labelTitle}
          value={getReadOnlyValue(initialOption)}
        ></ReadOnlyField>
      )}
    </>
  );
}
